import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import RichTextPreview from '../GenericFieldComponents/RichText/RichTextPreview';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import TextWithIcon from '../../TextWithIcon';
import {
  faCalendarDay,
  faCopy,
  faFileDownload,
  faFileLines,
  faHashtag,
  faTag,
  faUser
} from '@fortawesome/pro-regular-svg-icons';
import { formatDate } from '../../../utils/date';
import { useQuery } from '@tanstack/react-query';
import { fetchEditorTags } from '../../../api/queries';
import { useSubmissionFields } from '../../../hooks/useSubmissionFields';
import {
  FormFieldAuthors,
  FormFieldSubmittingAuthor
} from '@publishing-engine/domain/types/article';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonIcon, ButtonText, Modal, useToast } from '@morressier/alembic';
import {
  formatAuthorFull,
  formatAuthorLocation
} from '../../../utils/formatAuthor';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { downloadFile } from '../../../utils/file';
import { SubmissionTags } from '../../../pages/Organization/Workspace/Submissions/SubmissionTags';

const MAX_VISIBLE_AUTHORS = 1;

interface Props {
  isManuscriptId?: boolean;
}

const SubmissionInfoWithAuthors: React.FC<Props> = ({
  isManuscriptId = true
}) => {
  const {
    workflowResponse: { submissionId, workflowData, manuscriptId },
    fieldsResponse,
    submissionContext
  } = useSubmissionData();

  const { data: submissionTags } = useQuery({
    queryKey: ['editor-tags', submissionId],
    queryFn: () => (submissionId ? fetchEditorTags(submissionId) : null),
    enabled: !!submissionId,
    select: data =>
      (data?.response ?? []).filter(tag => tag.usage === 'submissions')
  });

  const toast = useToast();
  const enableConsolidatedPdf =
    submissionContext?.config?.enableConsolidatedPdf;
  const mansucriptValue = enableConsolidatedPdf
    ? (fieldsResponse?.fields?.AnonymizedConsolidatedPdf
        ?.value as ISubmissionFile)
    : (fieldsResponse.fields.Manuscript?.value as ISubmissionFile);

  const titlePageValue = fieldsResponse.fields.TitlePage
    ?.value as ISubmissionFile;

  const title = fieldsResponse.fields.Title!.value as string;

  const submissionDate = workflowData.states?.find(state =>
    ['initial', 'transferOffered'].includes(state.name)
  )?.completedWith?.date;

  const submissionType = fieldsResponse.articleTypeName;

  const { getFieldState } = useSubmissionFields('submission');
  const Authors = getFieldState<FormFieldAuthors>('Authors');
  const authors = Array.isArray(Authors?.value) ? Authors.value : [];
  const submittingAuthorEmail =
    getFieldState<FormFieldSubmittingAuthor>('SubmittingAuthor')?.value!;
  const submittingAuthorIndex = authors.findIndex(
    author => author.email === submittingAuthorEmail
  );
  const submittingAuthor =
    submittingAuthorIndex >= 0 ? authors[submittingAuthorIndex] : undefined;

  const [showAll, setShowAll] = useState(false);

  const remainingCount =
    authors.length > MAX_VISIBLE_AUTHORS
      ? authors.length - MAX_VISIBLE_AUTHORS
      : 0;

  const handleCopy = async (variable: string) => {
    await navigator.clipboard.writeText(variable);
    toast({
      variant: 'success',
      title: 'Copied to clipboard',
      description: '',
      isClosable: true
    });
  };

  return (
    <Flex backgroundColor="surfacePrimaryFill" gap={2} padding={4}>
      <Flex direction="column" gap={1} flex="1">
        <Box textStyle="headingItem">
          <RichTextPreview value={title} />
        </Box>

        <Flex gap={2} flexWrap="wrap">
          <TextWithIcon
            icon={faCalendarDay}
            text={formatDate(new Date(submissionDate ?? ''))}
          />
          <TextWithIcon icon={faFileLines} text={submissionType} />

          {isManuscriptId && (
            <TextWithIcon icon={faHashtag} text={manuscriptId} />
          )}
          {mansucriptValue?.url.length ? (
            <ButtonText onClick={() => downloadFile(mansucriptValue.url)}>
              <FontAwesomeIcon icon={faFileDownload} /> Manuscript
            </ButtonText>
          ) : null}
          {titlePageValue?.url.length && !enableConsolidatedPdf ? (
            <ButtonText onClick={() => downloadFile(titlePageValue.url)}>
              <FontAwesomeIcon icon={faFileDownload} /> Title page
            </ButtonText>
          ) : null}
        </Flex>

        {authors.length > 0 && (
          <Flex gap={2}>
            <Flex align="center">
              <TextWithIcon icon={faUser} />
              <Tooltip
                label={formatAuthorFull(submittingAuthor!)}
                isDisabled={!formatAuthorLocation(submittingAuthor!)}
                hasArrow
                placement="top"
              >
                <Text isTruncated textStyle="textContext">
                  <Text as="span" fontWeight="500">
                    {`${submittingAuthor?.firstName} ${submittingAuthor?.lastName}`}
                  </Text>
                  {formatAuthorLocation(submittingAuthor!)
                    ? `, ${formatAuthorLocation(submittingAuthor!)}`
                    : ''}
                </Text>
              </Tooltip>
              <ButtonIcon
                variant="primary"
                ariaLabel="copytoclipboard"
                icon={<FontAwesomeIcon icon={faCopy} />}
                size="small"
                onClick={() => handleCopy(formatAuthorFull(authors[0]!))}
              />
            </Flex>

            {remainingCount > 0 && (
              <>
                <Box
                  borderColor="neutralPrimaryBorder"
                  borderLeftWidth="1px"
                  borderStyle="solid"
                />
                <ButtonText
                  size="regular"
                  onClick={() => setShowAll(prev => !prev)}
                >
                  {`View +${remainingCount} Author${
                    remainingCount > 1 ? 's' : ''
                  }`}
                </ButtonText>
              </>
            )}
          </Flex>
        )}

        {submissionTags?.length ? (
          <Flex alignItems="center" gap={2}>
            <FontAwesomeIcon icon={faTag} flip="horizontal" />
            <SubmissionTags id="tag" tags={submissionTags} maxWidth="100%" />
          </Flex>
        ) : null}
        <Modal isOpen={showAll} width="720px" onClose={() => setShowAll(false)}>
          <Flex padding={4} flexDirection="column" gap={4}>
            <Text textStyle="headingGroup">Authors</Text>
            <Flex flexDirection="column">
              {authors.map((author, idx) => (
                <Flex key={idx} align="center" gap={1}>
                  <Tooltip
                    label={formatAuthorFull(author)}
                    isDisabled={!formatAuthorLocation(author)}
                    hasArrow
                    placement="top"
                  >
                    <Text isTruncated textStyle="textContext">
                      <Text as="span" fontWeight="500">
                        {`${author.firstName} ${author.lastName}`}
                      </Text>
                      {formatAuthorLocation(author)
                        ? `, ${formatAuthorLocation(author)}`
                        : ''}
                    </Text>
                  </Tooltip>
                  <ButtonIcon
                    variant="primary"
                    ariaLabel="copytoclipboard"
                    icon={<FontAwesomeIcon icon={faCopy} />}
                    size="small"
                    onClick={() => handleCopy(formatAuthorFull(author))}
                  />
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Modal>
      </Flex>
    </Flex>
  );
};

export default SubmissionInfoWithAuthors;
